import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { customerService } from '../services/firebase';
import { Customer } from '../types';
import CustomerCard from '../components/CustomerCard';
import LoadingSpinner from '../components/LoadingSpinner';
import LoadError from '../components/LoadError';
import { EyeIcon, EyeSlashIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

export default function Customers() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [query, setQuery] = useState('');

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const fetchedCustomers = await customerService.getAllCustomers(showInactive);
      setCustomers(fetchedCustomers);
    } catch (err) {
      console.error("Error fetching customers:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) =>
      [c.name, c.email, c.phone, c.address?.city]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [customers, query]);

  const activeCustomers = customers.filter(c => c.isActive !== false);
  const inactiveCustomers = customers.filter(c => c.isActive === false);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Customers</h1>
        <Link to="/customers/new" className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
          Add Customer
        </Link>
      </div>
      
      {/* Toggle for showing inactive customers */}
      <div className="mb-6 flex items-center space-x-3">
        <button
          onClick={() => setShowInactive(!showInactive)}
          className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
        >
          {showInactive ? (
            <>
              <EyeSlashIcon className="h-4 w-4 text-gray-600" />
              <span>Hide Deactivated Customers</span>
            </>
          ) : (
            <>
              <EyeIcon className="h-4 w-4 text-gray-600" />
              <span>Show Deactivated Customers</span>
            </>
          )}
        </button>
        
        {showInactive && (
          <span className="text-sm text-gray-600">
            Showing {customers.length} customers ({activeCustomers.length} active, {inactiveCustomers.length} deactivated)
          </span>
        )}
      </div>
      
      {!loading && !error && customers.length > 0 && (
        <div className="relative mb-6">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search customers by name, email, phone, or city…"
            aria-label="Search customers"
            className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-4 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          />
        </div>
      )}

      {loading ? (
        <div className="text-center">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <LoadError subject="customers" onRetry={fetchCustomers} />
      ) : customers.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-md">
          <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M15 21a6 6 0 00-9-5.197m0 0A5.975 5.975 0 0112 13a5.975 5.975 0 013 1.803M15 21a9 9 0 00-5.657-8.28M4 21a9 9 0 015.657-8.28m0 0V3m0 0a2 2 0 100 4 2 2 0 000-4z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No customers</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by adding a new customer.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {visible.map(customer => (
            <CustomerCard key={customer.id} customer={customer} />
          ))}
        </div>
      )}
    </div>
  );
} 